import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, X } from "lucide-react";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { PanelSelect } from "../../PanelSelect";
import type { UnfilledWay } from "../unfilledWays";
import type { AddRunFormProps, NewRun } from "./types";

const normalize = (value: string): string =>
  value.toUpperCase().replace(/[^A-Z0-9]/g, "");

export const AddRunForm = ({
  ways,
  boards,
  cableTypeOptions,
  onCreateCableType,
  takenKeys,
  onCreate,
  onCancel,
}: AddRunFormProps) => {
  const { t } = useTranslation();

  const [key, setKey] = useState("");
  const [sourceLabel, setSourceLabel] = useState(boards[0]?.label ?? "");
  const [cableType, setCableType] = useState("");
  // Carried from the schedule row he picked, never typed: they are the
  // schematic's own words, and inventing either is the precedence rule broken.
  const [fromWay, setFromWay] = useState<UnfilledWay | null>(null);

  const taken = useMemo(
    () => new Set(takenKeys.map(normalize).filter(Boolean)),
    [takenKeys],
  );

  const trimmedKey = key.trim();
  // Two runs printing one id are indistinguishable in every picker on the
  // panel, and the estimation keys its own edits by that id.
  const isDuplicate = !!trimmedKey && taken.has(normalize(trimmedKey));
  const board = boards.find((one) => one.label === sourceLabel);
  const canCreate = !!trimmedKey && !isDuplicate && !!board;

  const boardOptions = useMemo(
    () => boards.map((one) => ({ value: one.label, label: one.name })),
    [boards],
  );
  const cableTypeChoices = useMemo(
    () =>
      [...new Set([...cableTypeOptions, ...(cableType ? [cableType] : [])])]
        .sort((a, b) => a.localeCompare(b))
        .map((type) => ({ value: type, label: type })),
    [cableTypeOptions, cableType],
  );

  // A way he has already created is not offered again — filtered HERE, against
  // the same `taken` set the field validates on, so the list and the field can
  // never disagree about whether an id is free.
  const openWays = useMemo(
    () => ways.filter((way) => !taken.has(normalize(way.key))),
    [ways, taken],
  );

  const takeWay = (way: UnfilledWay) => {
    setFromWay(way);
    setKey(way.key);
    if (way.cableType) setCableType(way.cableType);
    // Only when the schedule's board is one of THIS plan's. A schematic names
    // boards the sheet may not carry, and a run silently created on the wrong
    // board is metres from the wrong place in the building.
    const named = boards.find(
      (one) => one.name === way.board || one.label === way.board,
    );
    if (named) setSourceLabel(named.label);
  };

  const create = () => {
    if (!canCreate || !board) return;
    const run: NewRun = {
      key: trimmedKey,
      sourceLabel: board.label,
      boardName: board.name,
      cableType: cableType.trim(),
      system: board.system,
      // Held only while the id is still the one that came off the schedule: he
      // retyped it, so the designator beside it is no longer its designator.
      ...(fromWay && normalize(fromWay.key) === normalize(trimmedKey)
        ? {
            ...(fromWay.breaker ? { breaker: fromWay.breaker } : {}),
            ...(fromWay.description
              ? { description: fromWay.description }
              : {}),
          }
        : {}),
    };
    trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_CABLE_RUN_ADDED, {
      fromSchematic:
        fromWay !== null && normalize(fromWay.key) === normalize(trimmedKey),
      ...(board.system !== undefined && { system: board.system }),
    });
    onCreate(run);
  };

  return (
    <div
      data-testid="add-run-form"
      className="mb-1.5 rounded-md border border-blue-300 bg-blue-50 p-2"
    >
      {openWays.length > 0 && (
        <div className="mb-2">
          <p className="text-[13px] font-medium text-slate-800">
            {t("smartSurveyor.cablesBeta.runAddFromSchematic", {
              count: openWays.length,
            })}
          </p>
          <ul className="mt-1 max-h-40 space-y-1 overflow-y-auto">
            {openWays.map((way) => (
              <li key={`${way.board}:${way.key}`}>
                <button
                  type="button"
                  onClick={() => takeWay(way)}
                  className="flex w-full items-baseline gap-2 rounded border border-slate-300 bg-white px-2 py-1.5 text-left hover:border-blue-500 hover:bg-white"
                >
                  <span className="flex-none text-[14px] font-semibold tabular-nums text-slate-900">
                    {way.key}
                  </span>
                  <span className="min-w-0 truncate text-[13px] text-slate-600">
                    {[way.board, way.cableType, way.description]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                  {way.tagOnPlan === false && (
                    // The one fact that changes what he is agreeing to: the
                    // schedule names this way and the sheet never tags it, so
                    // the devices on it are his to find rather than to confirm.
                    <span className="ml-auto flex-none whitespace-nowrap rounded bg-slate-200 px-1.5 py-0.5 text-[13px] font-medium text-slate-700">
                      {t("smartSurveyor.cablesBeta.runAddTagNotOnPlan")}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <label
        className="block text-[13px] font-medium text-slate-800"
        htmlFor="add-run-key"
      >
        {t("smartSurveyor.cablesBeta.runAddKey")}
      </label>
      <input
        id="add-run-key"
        autoFocus
        value={key}
        aria-label={t("smartSurveyor.cablesBeta.runAddKey")}
        onChange={(event) => setKey(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") create();
          if (event.key === "Escape") onCancel();
        }}
        className="mt-1 h-9 w-full rounded border border-slate-300 px-2 text-[14px] text-slate-800 focus:border-blue-500 focus:outline-none"
      />
      {isDuplicate && (
        <p className="mt-1 text-[13px] font-medium text-red-700">
          {t("smartSurveyor.cablesBeta.runAddKeyTaken")}
        </p>
      )}

      <div className="mt-1.5 flex items-center gap-2">
        <span className="w-20 flex-none text-[13px] font-medium text-slate-800">
          {t("smartSurveyor.cablesBeta.factSource")}
        </span>
        <span className="min-w-0 flex-1">
          <PanelSelect
            value={sourceLabel}
            options={boardOptions}
            placeholder={t("smartSurveyor.cablesBeta.factSourcePick")}
            ariaLabel={t("smartSurveyor.cablesBeta.factSource")}
            onPick={setSourceLabel}
          />
        </span>
      </div>

      <div className="mt-1.5 flex items-center gap-2">
        <span className="w-20 flex-none text-[13px] font-medium text-slate-800">
          {t("smartSurveyor.cablesBeta.factCableType")}
        </span>
        <span className="min-w-0 flex-1">
          <PanelSelect
            value={cableType}
            options={cableTypeChoices}
            placeholder={t("smartSurveyor.cablesBeta.cableTypePick")}
            ariaLabel={t("smartSurveyor.cablesBeta.factCableType")}
            onPick={setCableType}
            onCreate={(name) => {
              const trimmed = name.trim();
              if (!trimmed) return;
              setCableType(trimmed);
              onCreateCableType?.(trimmed);
            }}
            createLabel={(text) => t("smartSurveyor.cablesBeta.cableTypesCreate", { name: text })}
          />
        </span>
      </div>

      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={create}
          disabled={!canCreate}
          className="flex h-9 flex-none items-center gap-1.5 rounded-md bg-blue-600 px-2.5 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:bg-slate-300"
        >
          <Plus size={15} />
          {t("smartSurveyor.cablesBeta.runAddCreate")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex h-9 flex-none items-center gap-1 px-1 text-[13px] font-medium text-slate-600 hover:text-slate-900"
        >
          <X size={14} />
          {t("smartSurveyor.cablesBeta.cancel")}
        </button>
      </div>
    </div>
  );
};
